import React, { useState, useEffect, useRef } from 'react';
import { useUser } from '../components/UserProvider';
import { useToast } from '../components/Toast';
import { useNavigate } from 'react-router-dom';
import './accountsettings.css';
import { hashPassword, secureCleanup, validatePasswordStrengthClient } from '../utils/crypto';

export default function AccountSettings() {
  const { user, getAuthHeaders, updateUser, logout } = useUser();
  const { success, error: showError, warning: showWarning } = useToast();
  const navigate = useNavigate();
  const deleteConfirmationRef = useRef(null);
  
  const [userInfo, setUserInfo] = useState({
    username: '',
    email: '',
    createdAt: '',
    lastLogin: '',
    darkMode: false
  });
  const [passwordData, setPasswordData] = useState({
    currentPassword: '',
    newPassword: '',
    confirmPassword: ''
  });
  const [showPasswordSection, setShowPasswordSection] = useState(false);
  const [showCurrentPassword, setShowCurrentPassword] = useState(false);
  const [showNewPassword, setShowNewPassword] = useState(false);
  const [showConfirmPassword, setShowConfirmPassword] = useState(false);
  const [passwordStrength, setPasswordStrength] = useState(0);
  const [passwordFeedback, setPasswordFeedback] = useState([]);
  const [showDeleteSection, setShowDeleteSection] = useState(false);
  const [deleteConfirmation, setDeleteConfirmation] = useState({
    password: '',
    confirmation: '',
    agreeToDelete: false
  });
  const [isDeleteFromButton, setIsDeleteFromButton] = useState(false);
  const [loading, setLoading] = useState(false);

  // 비밀번호 강도 계산 함수
  const calculatePasswordStrength = (password) => {
    return validatePasswordStrengthClient(password);
  };

  // 비밀번호 토글 함수들
  const toggleCurrentPasswordVisibility = () => {
    setShowCurrentPassword(!showCurrentPassword);
  };

  const toggleNewPasswordVisibility = () => {
    setShowNewPassword(!showNewPassword);
  };

  const toggleConfirmPasswordVisibility = () => {
    setShowConfirmPassword(!showConfirmPassword);
  };

  // 사용자 정보 불러오기
  useEffect(() => {
    const fetchUserInfo = async () => {
      try {
        const response = await fetch(`/api/user/profile`, {
          headers: getAuthHeaders()
        });

        if (response.ok) {
          const data = await response.json();
          setUserInfo({
            username: data.username,
            email: data.email,
            createdAt: new Date(data.created_at).toLocaleDateString('ko-KR'),
            lastLogin: data.last_login ? new Date(data.last_login).toLocaleString('ko-KR') : '없음',
            darkMode: Boolean(data.dark_mode) // TINYINT를 Boolean으로 변환
          });
        }
      } catch (error) {
        console.error('사용자 정보 불러오기 오류:', error);
        showError('사용자 정보를 불러올 수 없습니다.');
      }
    };

    if (user) {
      fetchUserInfo();
    }
  }, [user, getAuthHeaders, showError]);

  // 다크모드 토글
  const handleDarkModeToggle = () => {
    setUserInfo(prev => ({
      ...prev,
      darkMode: !prev.darkMode
    }));
  };

  // 설정 저장
  const handleSaveSettings = async () => {
    setLoading(true);

    try {
      const response = await fetch(`/api/user/settings`, {
        method: 'PUT',
        headers: {
          'Content-Type': 'application/json',
          ...getAuthHeaders()
        },
        body: JSON.stringify({
          darkMode: userInfo.darkMode
        })
      });

      if (response.ok) {
        // UserProvider의 사용자 정보 업데이트
        updateUser({ ...user, dark_mode: userInfo.darkMode });
        
        success('설정이 저장되었습니다.');
      } else {
        const errorData = await response.json();
        showError(errorData.error || '설정 저장에 실패했습니다.');
      }
    } catch (error) {
      console.error('설정 저장 오류:', error);
      showError('네트워크 오류로 설정 저장에 실패했습니다.');
    } finally {
      setLoading(false);
    }
  };

  // 비밀번호 변경
  const handlePasswordChange = async (e) => {
    e.preventDefault();
    
    if (passwordData.newPassword !== passwordData.confirmPassword) {
      showError('새 비밀번호가 일치하지 않습니다.');
      return;
    }

    // 강화된 비밀번호 검증
    const passwordCheck = validatePasswordStrengthClient(passwordData.newPassword);
    if (!passwordCheck.isValid) {
      showError(passwordCheck.feedback.join(', '));
      return;
    }

    setLoading(true);

    // 보안을 위한 비밀번호 해싱 (새로운 통일된 방식)
    const currentHashedPassword = hashPassword(passwordData.currentPassword, user.username, 'login');
    const newHashedPassword = hashPassword(passwordData.newPassword, user.username, 'login');

    // 메모리에서 비밀번호 즉시 제거
    setPasswordData({
      currentPassword: '',
      newPassword: '',
      confirmPassword: ''
    });

    try {
      const response = await fetch(`/api/user/change-password`, {
        method: 'PUT',
        headers: {
          'Content-Type': 'application/json',
          ...getAuthHeaders()
        },
        body: JSON.stringify({
          currentHashedPassword: currentHashedPassword,
          newHashedPassword: newHashedPassword
        })
      });

      // 보안 정리 (해싱된 비밀번호도 메모리에서 제거)
      const cleanupObj = { currentHashedPassword, newHashedPassword };
      secureCleanup(cleanupObj, ['currentHashedPassword', 'newHashedPassword']);

      if (response.ok) {
        success('비밀번호가 성공적으로 변경되었습니다.');
        setPasswordData({
          currentPassword: '',
          newPassword: '',
          confirmPassword: ''
        });
        setShowPasswordSection(false);
        // 비밀번호 표시 상태도 초기화
        setShowCurrentPassword(false);
        setShowNewPassword(false);
        setShowConfirmPassword(false);
        setPasswordStrength(0);
        setPasswordFeedback([]);
      } else {
        const errorData = await response.json();
        showError(errorData.error || '비밀번호 변경에 실패했습니다.');
      }
    } catch (error) {
      console.error('비밀번호 변경 오류:', error);
      showError('네트워크 오류로 비밀번호 변경에 실패했습니다.');
    } finally {
      setLoading(false);
    }
  };

  // 회원 탈퇴 버튼 클릭
  const handleShowDeleteSection = () => {
    setIsDeleteFromButton(true);
    setShowDeleteSection(true);
    
    // 다른 섹션들 닫기
    setShowPasswordSection(false);
    
    // 삭제 섹션으로 스크롤
    setTimeout(() => {
      if (deleteConfirmationRef.current) {
        deleteConfirmationRef.current.scrollIntoView({ behavior: 'smooth' });
      }
    }, 100);
  };

  // 회원 탈퇴 취소
  const handleCancelDelete = () => {
    setShowDeleteSection(false);
    setIsDeleteFromButton(false);
    setDeleteConfirmation({
      password: '',
      confirmation: '',
      agreeToDelete: false
    });
  };

  // 회원 탈퇴 실행
  const handleDeleteAccount = async (e) => {
    e.preventDefault();
    
    // 버튼으로 접근하지 않은 경우 차단
    if (!isDeleteFromButton) {
      showError('잘못된 접근입니다. 회원 탈퇴 버튼을 통해 접근해주세요.');
      return;
    }

    // 확인 문구 검증
    if (deleteConfirmation.confirmation !== '계정을 삭제합니다') {
      showError('확인 문구를 정확히 입력해주세요.');
      return;
    }

    // 동의 체크 검증
    if (!deleteConfirmation.agreeToDelete) {
      showError('계정 삭제에 동의해주세요.');
      return;
    }

    // 비밀번호 입력 검증
    if (!deleteConfirmation.password) {
      showError('비밀번호를 입력해주세요.');
      return;
    }

    setLoading(true);

    // 보안을 위한 비밀번호 해싱
    const hashedPassword = hashPassword(deleteConfirmation.password, user.username, 'login');

    // 메모리에서 비밀번호 즉시 제거
    setDeleteConfirmation(prev => ({
      ...prev,
      password: ''
    }));

    try {
      const response = await fetch(`/api/user/delete-account`, {
        method: 'DELETE',
        headers: {
          'Content-Type': 'application/json',
          ...getAuthHeaders()
        },
        body: JSON.stringify({
          hashedPassword: hashedPassword,
          confirmationText: deleteConfirmation.confirmation
        })
      });

      if (response.ok) {
        showWarning('계정이 성공적으로 삭제되었습니다. 잠시 후 메인 페이지로 이동합니다.');
        
        // 3초 후 로그아웃 및 메인 페이지로 이동
        setTimeout(() => {
          logout();
          navigate('/');
        }, 3000);
      } else {
        const errorData = await response.json();
        showError(errorData.error || '계정 삭제에 실패했습니다.');
      }
    } catch (error) {
      console.error('계정 삭제 오류:', error);
      showError('네트워크 오류로 계정 삭제에 실패했습니다.');
    } finally {
      setLoading(false);
    }
  };

  if (!user) {
    return (
      <div className="account-settings">
        <div className="loading">로그인이 필요합니다.</div>
      </div>
    );
  }

  return (
    <div className="account-settings">
      <div className="account-settings-container">
        <h1>계정 설정</h1>

        {/* 계정 정보 섹션 */}
        <div className="account-settings-section">
          <h3>계정 정보</h3>
          <div className="account-info-grid">
            <div className="account-info-item">
              <label>아이디</label>
              <div className="account-info-value">{userInfo.username}</div>
            </div>
            <div className="account-info-item">
              <label>이메일</label>
              <div className="account-info-value">{userInfo.email}</div>
            </div>
            <div className="account-info-item">
              <label>가입일</label>
              <div className="account-info-value">{userInfo.createdAt}</div>
            </div>
            <div className="account-info-item">
              <label>마지막 로그인</label>
              <div className="account-info-value">{userInfo.lastLogin}</div>
            </div>
          </div>
        </div>

        {/* 테마 설정 섹션 */}
        <div className="account-settings-section">
          <h3>테마 설정</h3>
          <div className="theme-setting">
            <label className="theme-toggle">
              <span>다크 모드</span>
              <div className="toggle-switch">
                <input
                  type="checkbox"
                  checked={userInfo.darkMode}
                  onChange={handleDarkModeToggle}
                />
                <span className="toggle-slider"></span>
              </div>
            </label>
          </div>
          <button 
            className="settings-save-btn"
            onClick={handleSaveSettings}
            disabled={loading}
          >
            {loading ? '저장 중...' : '설정 저장'}
          </button>
        </div>

        {/* 비밀번호 변경 섹션 */}
        <div className="account-settings-section">
          <h3>보안</h3>
          <div className="password-section">
            {!showPasswordSection ? (
              <button 
                className="change-password-btn"
                onClick={() => setShowPasswordSection(true)}
              >
                비밀번호 변경
              </button>
            ) : (
              <form onSubmit={handlePasswordChange} className="password-form">
                <div className="form-group">
                  <label htmlFor="current-password">현재 비밀번호</label>
                  <div className="password-input-container">
                    <input
                      type={showCurrentPassword ? "text" : "password"}
                      id="current-password"
                      value={passwordData.currentPassword}
                      onChange={(e) => setPasswordData(prev => ({
                        ...prev,
                        currentPassword: e.target.value
                      }))}
                      required
                      autoComplete="current-password"
                      spellCheck="false"
                      autoCorrect="off"
                      autoCapitalize="off"
                    />
                    <button
                      type="button"
                      className="password-toggle-btn"
                      onClick={toggleCurrentPasswordVisibility}
                      aria-label={showCurrentPassword ? "비밀번호 숨기기" : "비밀번호 보기"}
                    >
                      {showCurrentPassword ? (
                        <svg width="20" height="20" viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg">
                          <path d="M2.99902 3L20.999 21M9.8433 9.91364C9.32066 10.4536 8.99902 11.1892 8.99902 12C8.99902 13.6569 10.3422 15 11.999 15C12.8215 15 13.5667 14.669 14.1086 14.133M6.49902 6.64715C4.59972 7.90034 3.15305 9.78394 2.45703 12C3.73128 16.0571 7.52159 19 11.9992 19C13.9881 19 15.8414 18.4194 17.3988 17.4184M10.999 5.04939C11.328 5.01673 11.6617 5 11.9992 5C16.4769 5 20.2672 7.94291 21.5414 12C21.2607 12.894 20.8577 13.7338 20.3522 14.5" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"/>
                        </svg>
                      ) : (
                        <svg width="20" height="20" viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg">
                          <path d="M2.45703 12C3.73128 7.94288 7.52159 5 11.9992 5C16.4769 5 20.2672 7.94291 21.5414 12C20.2672 16.0571 16.4769 19 11.9992 19C7.52159 19 3.73128 16.0571 2.45703 12Z" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"/>
                          <path d="M12 15C13.6569 15 15 13.6569 15 12C15 10.3431 13.6569 9 12 9C10.3431 9 9 10.3431 9 12C9 13.6569 10.3431 15 12 15Z" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"/>
                        </svg>
                      )}
                    </button>
                  </div>
                </div>
                
                <div className="form-group">
                  <label htmlFor="new-password">새 비밀번호</label>
                  <div className="password-input-container">
                    <input
                      type={showNewPassword ? "text" : "password"}
                      id="new-password"
                      value={passwordData.newPassword}
                      onChange={(e) => {
                        const newPassword = e.target.value;
                        setPasswordData(prev => ({
                          ...prev,
                          newPassword: newPassword
                        }));
                        
                        // 비밀번호 강도 계산
                        if (newPassword) {
                          const { strength, feedback } = calculatePasswordStrength(newPassword);
                          setPasswordStrength(strength);
                          setPasswordFeedback(feedback);
                        } else {
                          setPasswordStrength(0);
                          setPasswordFeedback([]);
                        }
                      }}
                      minLength="8"
                      required
                      autoComplete="new-password"
                      spellCheck="false"
                      autoCorrect="off"
                      autoCapitalize="off"
                    />
                    <button
                      type="button"
                      className="password-toggle-btn"
                      onClick={toggleNewPasswordVisibility}
                      aria-label={showNewPassword ? "비밀번호 숨기기" : "비밀번호 보기"}
                    >
                      {showNewPassword ? (
                        <svg width="20" height="20" viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg">
                          <path d="M2.99902 3L20.999 21M9.8433 9.91364C9.32066 10.4536 8.99902 11.1892 8.99902 12C8.99902 13.6569 10.3422 15 11.999 15C12.8215 15 13.5667 14.669 14.1086 14.133M6.49902 6.64715C4.59972 7.90034 3.15305 9.78394 2.45703 12C3.73128 16.0571 7.52159 19 11.9992 19C13.9881 19 15.8414 18.4194 17.3988 17.4184M10.999 5.04939C11.328 5.01673 11.6617 5 11.9992 5C16.4769 5 20.2672 7.94291 21.5414 12C21.2607 12.894 20.8577 13.7338 20.3522 14.5" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"/>
                        </svg>
                      ) : (
                        <svg width="20" height="20" viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg">
                          <path d="M2.45703 12C3.73128 7.94288 7.52159 5 11.9992 5C16.4769 5 20.2672 7.94291 21.5414 12C20.2672 16.0571 16.4769 19 11.9992 19C7.52159 19 3.73128 16.0571 2.45703 12Z" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"/>
                          <path d="M12 15C13.6569 15 15 13.6569 15 12C15 10.3431 13.6569 9 12 9C10.3431 9 9 10.3431 9 12C9 13.6569 10.3431 15 12 15Z" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"/>
                        </svg>
                      )}
                    </button>
                  </div>
                  
                  {/* 비밀번호 강도 표시기 */}
                  {passwordData.newPassword && (
                    <div className={`password-strength-container strength-${passwordStrength}`}>
                      <div className="password-strength-bar">
                        <div 
                          className={`password-strength-fill strength-${passwordStrength}`}
                          style={{ width: `${(passwordStrength / 5) * 100}%` }}
                        ></div>
                      </div>
                      <div className="password-strength-text">
                        {passwordStrength === 5 ? '매우 강함' :
                         passwordStrength === 4 ? '강함' :
                         passwordStrength === 3 ? '보통' :
                         passwordStrength === 2 ? '약함' : '매우 약함'}
                      </div>
                      {passwordFeedback.length > 0 && (
                        <div className="password-feedback">
                          <small>필요: {passwordFeedback.join(', ')}</small>
                        </div>
                      )}
                    </div>
                  )}
                </div>
                
                <div className="form-group">
                  <label htmlFor="confirm-password">새 비밀번호 확인</label>
                  <div className="password-input-container">
                    <input
                      type={showConfirmPassword ? "text" : "password"}
                      id="confirm-password"
                      value={passwordData.confirmPassword}
                      onChange={(e) => setPasswordData(prev => ({
                        ...prev,
                        confirmPassword: e.target.value
                      }))}
                      minLength="8"
                      required
                      autoComplete="new-password"
                      spellCheck="false"
                      autoCorrect="off"
                      autoCapitalize="off"
                    />
                    <button
                      type="button"
                      className="password-toggle-btn"
                      onClick={toggleConfirmPasswordVisibility}
                      aria-label={showConfirmPassword ? "비밀번호 숨기기" : "비밀번호 보기"}
                    >
                      {showConfirmPassword ? (
                        <svg width="20" height="20" viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg">
                          <path d="M2.99902 3L20.999 21M9.8433 9.91364C9.32066 10.4536 8.99902 11.1892 8.99902 12C8.99902 13.6569 10.3422 15 11.999 15C12.8215 15 13.5667 14.669 14.1086 14.133M6.49902 6.64715C4.59972 7.90034 3.15305 9.78394 2.45703 12C3.73128 16.0571 7.52159 19 11.9992 19C13.9881 19 15.8414 18.4194 17.3988 17.4184M10.999 5.04939C11.328 5.01673 11.6617 5 11.9992 5C16.4769 5 20.2672 7.94291 21.5414 12C21.2607 12.894 20.8577 13.7338 20.3522 14.5" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"/>
                        </svg>
                      ) : (
                        <svg width="20" height="20" viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg">
                          <path d="M2.45703 12C3.73128 7.94288 7.52159 5 11.9992 5C16.4769 5 20.2672 7.94291 21.5414 12C20.2672 16.0571 16.4769 19 11.9992 19C7.52159 19 3.73128 16.0571 2.45703 12Z" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"/>
                          <path d="M12 15C13.6569 15 15 13.6569 15 12C15 10.3431 13.6569 9 12 9C10.3431 9 9 10.3431 9 12C9 13.6569 10.3431 15 12 15Z" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"/>
                        </svg>
                      )}
                    </button>
                  </div>
                </div>
                <div className="password-form-buttons">
                  <button type="submit" className="password-save-btn" disabled={loading}>
                    {loading ? '변경 중...' : '비밀번호 변경'}
                  </button>
                  <button 
                    type="button" 
                    className="password-cancel-btn"
                    onClick={() => {
                      setShowPasswordSection(false);
                      setPasswordData({
                        currentPassword: '',
                        newPassword: '',
                        confirmPassword: ''
                      });
                    }}
                  >
                    취소
                  </button>
                </div>
              </form>
            )}
          </div>
        </div>

        {/* 회원 탈퇴 섹션 */}
        <div className="account-settings-section danger-section">
          <h3>위험 구역</h3>
          <div className="danger-content">
            <div className="danger-warning">
              <h4>⚠️ 계정 삭제</h4>
              <p>계정을 삭제하면 모든 데이터가 영구적으로 삭제되며 복구할 수 없습니다.</p>
            </div>
            
            {!showDeleteSection ? (
              <button 
                className="delete-account-btn"
                onClick={handleShowDeleteSection}
              >
                회원 탈퇴
              </button>
            ) : (
              <div ref={deleteConfirmationRef} className="delete-confirmation-form">
                <div className="delete-warning-box">
                  <h4>🚨 계정 삭제 확인</h4>
                  <p>정말로 계정을 삭제하시겠습니까? 이 작업은 되돌릴 수 없습니다.</p>
                  <ul>
                    <li>모든 개인 정보가 삭제됩니다</li>
                    <li>작성한 모든 데이터가 삭제됩니다</li>
                    <li>계정 복구가 불가능합니다</li>
                  </ul>
                </div>

                <form onSubmit={handleDeleteAccount} className="delete-form">
                  <div className="form-group">
                    <label htmlFor="delete-password">비밀번호 확인</label>
                    <input
                      type="password"
                      id="delete-password"
                      value={deleteConfirmation.password}
                      onChange={(e) => setDeleteConfirmation(prev => ({
                        ...prev,
                        password: e.target.value
                      }))}
                      placeholder="계정 삭제를 위해 비밀번호를 입력하세요"
                      required
                    />
                  </div>

                  <div className="form-group">
                    <label htmlFor="delete-confirmation">확인 문구 입력</label>
                    <input
                      type="text"
                      id="delete-confirmation"
                      value={deleteConfirmation.confirmation}
                      onChange={(e) => setDeleteConfirmation(prev => ({
                        ...prev,
                        confirmation: e.target.value
                      }))}
                      placeholder="계정을 삭제합니다"
                      required
                    />
                    <small>위의 문구를 정확히 입력해주세요: "계정을 삭제합니다"</small>
                  </div>

                  <div className="form-group checkbox-group">
                    <label className="checkbox-label">
                      <input
                        type="checkbox"
                        checked={deleteConfirmation.agreeToDelete}
                        onChange={(e) => setDeleteConfirmation(prev => ({
                          ...prev,
                          agreeToDelete: e.target.checked
                        }))}
                        required
                      />
                      <span>계정 삭제에 따른 모든 결과를 이해하고 동의합니다</span>
                    </label>
                  </div>
                  <div className="delete-form-buttons">
                    <button 
                      type="submit" 
                      className="confirm-delete-btn" 
                      disabled={loading || !deleteConfirmation.agreeToDelete}
                    >
                      {loading ? '삭제 중...' : '계정 영구 삭제'}
                    </button>
                    <button 
                      type="button" 
                      className="cancel-delete-btn"
                      onClick={handleCancelDelete}
                      disabled={loading}
                    >
                      취소
                    </button>
                  </div>
                </form>
              </div>
            )}
          </div>
        </div>
      </div>
    </div>
  );
}
